import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Archive, Plus, Loader2, X, Shield, Lock, Unlock, Trash2, FileText,
} from 'lucide-react'
import { apiGet, apiPost, apiDelete } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'

const TRIGGER_LABELS: Record<string, string> = {
  upload_date: 'From upload date',
  custom_date: 'From custom date field',
}

const DISPOSITION_LABELS: Record<string, string> = {
  review: 'Review before disposal',
  archive: 'Auto-archive',
  delete: 'Auto-delete',
}

export function RetentionPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'policies' | 'holds'>('policies')
  const [showPolicyModal, setShowPolicyModal] = useState(false)
  const [showHoldModal, setShowHoldModal] = useState(false)
  const [selectedHold, setSelectedHold] = useState<any | null>(null)
  const [addDocIds, setAddDocIds] = useState('')

  // ── Policies ──────────────────────────────────────────────────────────
  const { data: policies, isLoading: policiesLoading } = useQuery({
    queryKey: ['docflow-retention-policies'],
    queryFn: () => apiGet<any[]>('/docflow/retention/policies'),
  })

  const [newPolicy, setNewPolicy] = useState({
    name: '', retention_days: 365, trigger_event: 'upload_date',
    disposition_action: 'review', applies_to_classification: '', custom_date_field: '',
  })

  const createPolicyMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/docflow/retention/policies', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-retention-policies'] })
      setShowPolicyModal(false)
      setNewPolicy({ name: '', retention_days: 365, trigger_event: 'upload_date', disposition_action: 'review', applies_to_classification: '', custom_date_field: '' })
    },
  })

  const deletePolicyMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/docflow/retention/policies/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['docflow-retention-policies'] }),
  })

  // ── Legal Holds ───────────────────────────────────────────────────────
  const { data: holds, isLoading: holdsLoading } = useQuery({
    queryKey: ['docflow-legal-holds'],
    queryFn: () => apiGet<any[]>('/docflow/retention/holds'),
  })

  const { data: holdDetail } = useQuery({
    queryKey: ['docflow-hold-detail', selectedHold?.id],
    queryFn: () => apiGet<any>(`/docflow/retention/holds/${selectedHold.id}`),
    enabled: !!selectedHold?.id,
  })

  const [newHold, setNewHold] = useState({ name: '', reason: '', description: '' })

  const createHoldMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/docflow/retention/holds', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-legal-holds'] })
      setShowHoldModal(false)
      setNewHold({ name: '', reason: '', description: '' })
    },
  })

  const releaseMutation = useMutation({
    mutationFn: (holdId: string) => apiPost(`/docflow/retention/holds/${holdId}/release`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-legal-holds'] })
      queryClient.invalidateQueries({ queryKey: ['docflow-hold-detail'] })
    },
  })

  const addDocsMutation = useMutation({
    mutationFn: (data: { holdId: string; ids: string[] }) =>
      apiPost(`/docflow/retention/holds/${data.holdId}/add-documents`, { document_ids: data.ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-hold-detail'] })
      setAddDocIds('')
    },
  })

  const policyList = Array.isArray(policies) ? policies : []
  const holdList = Array.isArray(holds) ? holds : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Document Retention</h1>
          <p className="text-gray-500 text-sm mt-0.5">Retention policies and legal holds</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('policies')}
          className={cn('px-4 py-2 rounded-md text-sm font-medium transition-colors',
            activeTab === 'policies' ? 'bg-white text-navy-800 shadow-sm' : 'text-gray-500 hover:text-navy-800'
          )}
        >
          <Archive className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Retention Policies
        </button>
        <button
          onClick={() => setActiveTab('holds')}
          className={cn('px-4 py-2 rounded-md text-sm font-medium transition-colors',
            activeTab === 'holds' ? 'bg-white text-navy-800 shadow-sm' : 'text-gray-500 hover:text-navy-800'
          )}
        >
          <Lock className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Legal Holds ({holdList.length})
        </button>
      </div>

      {/* ── Policies Tab ─────────────────────────────────────────────────── */}
      {activeTab === 'policies' && (
        <>
          <div className="flex justify-end">
            <button onClick={() => setShowPolicyModal(true)} className="btn-primary">
              <Plus className="w-4 h-4" /> Add Policy
            </button>
          </div>

          {policiesLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
            </div>
          ) : policyList.length === 0 ? (
            <div className="card text-center py-16">
              <Archive className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No retention policies configured</p>
              <p className="text-xs text-gray-400 mt-1">Add policies to automate document lifecycle management</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {policyList.map((policy: any) => {
                const years = Math.round(policy.retention_days / 365)
                return (
                  <div key={policy.id} className="card">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-sm font-semibold text-navy-800">{policy.name}</h3>
                        {policy.applies_to_classification && (
                          <p className="text-xs text-gray-400 font-mono mt-0.5">{policy.applies_to_classification}</p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          if (confirm(`Delete policy "${policy.name}"?`)) deletePolicyMutation.mutate(policy.id)
                        }}
                        className="text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Retention</span>
                        <span className="text-navy-800 font-medium">
                          {years >= 1 ? `${years} year${years > 1 ? 's' : ''}` : `${policy.retention_days} days`}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Trigger</span>
                        <span className="text-navy-800">{TRIGGER_LABELS[policy.trigger_event] ?? policy.trigger_event}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Disposition</span>
                        <span className="text-navy-800">{DISPOSITION_LABELS[policy.disposition_action] ?? policy.disposition_action}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* ── Legal Holds Tab ───────────────────────────────────────────────── */}
      {activeTab === 'holds' && (
        <>
          <div className="flex justify-end">
            <button onClick={() => setShowHoldModal(true)} className="btn-primary">
              <Plus className="w-4 h-4" /> Create Legal Hold
            </button>
          </div>

          {holdsLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
            </div>
          ) : holdList.length === 0 ? (
            <div className="card text-center py-16">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No legal holds active</p>
              <p className="text-xs text-gray-400 mt-1">Create a legal hold to prevent document deletion during disputes or investigations</p>
            </div>
          ) : (
            <div className="space-y-3">
              {holdList.map((hold: any) => (
                <div
                  key={hold.id}
                  className={cn('card cursor-pointer hover:border-navy-300 transition-colors',
                    hold.status === 'released' && 'opacity-60'
                  )}
                  onClick={() => setSelectedHold(hold)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {hold.status === 'active' ? (
                          <Lock className="w-4 h-4 text-red-500" />
                        ) : (
                          <Unlock className="w-4 h-4 text-green-500" />
                        )}
                        <h3 className="text-sm font-semibold text-navy-800">{hold.name}</h3>
                        <span className={cn('badge text-xs',
                          hold.status === 'active' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        )}>
                          {hold.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{hold.reason}</p>
                    </div>
                    <p className="text-xs text-gray-400">{formatDate(hold.created_at)}</p>
                  </div>
                  {hold.document_count != null && (
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> {hold.document_count} documents held
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Hold Detail Slide-over */}
      {selectedHold && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="bg-white w-full max-w-lg shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-navy-800 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-red-500" />
                  {selectedHold.name}
                </h2>
                <p className="text-sm text-gray-500">{selectedHold.reason}</p>
              </div>
              <button onClick={() => setSelectedHold(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {holdDetail?.description && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1">Description</h3>
                  <p className="text-sm text-navy-800">{holdDetail.description}</p>
                </div>
              )}

              {holdDetail?.documents?.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Held Documents</h3>
                  <div className="space-y-1">
                    {holdDetail.documents.map((doc: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-navy-800">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span>{doc.title ?? doc.filename ?? doc.id}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add documents to hold */}
              {selectedHold.status === 'active' && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Add Documents</h3>
                  <div className="flex gap-2">
                    <input
                      value={addDocIds}
                      onChange={(e) => setAddDocIds(e.target.value)}
                      placeholder="Document IDs (comma-separated)"
                      className="input flex-1"
                    />
                    <button
                      onClick={() => {
                        const ids = addDocIds.split(',').map(s => s.trim()).filter(Boolean)
                        if (ids.length) addDocsMutation.mutate({ holdId: selectedHold.id, ids })
                      }}
                      disabled={!addDocIds.trim() || addDocsMutation.isPending}
                      className="btn-primary"
                    >
                      {addDocsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {selectedHold.status === 'active' && (
              <div className="px-6 py-4 border-t flex justify-end">
                <button
                  onClick={() => {
                    if (confirm('Release this legal hold? Documents will no longer be protected from deletion.')) {
                      releaseMutation.mutate(selectedHold.id)
                    }
                  }}
                  disabled={releaseMutation.isPending}
                  className="btn-secondary text-sm text-red-600 border-red-200 hover:bg-red-50"
                >
                  {releaseMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
                  Release Hold
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Policy Modal */}
      {showPolicyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800 flex items-center gap-2">
                <Archive className="w-5 h-5 text-blue-600" />
                New Retention Policy
              </h2>
              <button onClick={() => setShowPolicyModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (!newPolicy.name) return
                const payload: Record<string, unknown> = {
                  name: newPolicy.name,
                  retention_days: newPolicy.retention_days,
                  trigger_event: newPolicy.trigger_event,
                  disposition_action: newPolicy.disposition_action,
                }
                if (newPolicy.applies_to_classification) payload.applies_to_classification = newPolicy.applies_to_classification
                if (newPolicy.trigger_event === 'custom_date' && newPolicy.custom_date_field) {
                  payload.custom_date_field = newPolicy.custom_date_field
                }
                createPolicyMutation.mutate(payload)
              }}
              className="space-y-4"
            >
              <div>
                <label className="label">Policy Name *</label>
                <input
                  value={newPolicy.name}
                  onChange={(e) => setNewPolicy({ ...newPolicy, name: e.target.value })}
                  className="input" placeholder="e.g. H&S Records — 40 Years" required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Retention (days) *</label>
                  <input
                    type="number" min={1}
                    value={newPolicy.retention_days}
                    onChange={(e) => setNewPolicy({ ...newPolicy, retention_days: parseInt(e.target.value) || 365 })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Trigger Event</label>
                  <select
                    value={newPolicy.trigger_event}
                    onChange={(e) => setNewPolicy({ ...newPolicy, trigger_event: e.target.value })}
                    className="input"
                  >
                    <option value="upload_date">Upload date</option>
                    <option value="custom_date">Custom date field</option>
                  </select>
                </div>
              </div>
              {newPolicy.trigger_event === 'custom_date' && (
                <div>
                  <label className="label">Custom Date Field</label>
                  <input
                    value={newPolicy.custom_date_field}
                    onChange={(e) => setNewPolicy({ ...newPolicy, custom_date_field: e.target.value })}
                    className="input" placeholder="e.g. expiry_date"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Disposition Action</label>
                  <select
                    value={newPolicy.disposition_action}
                    onChange={(e) => setNewPolicy({ ...newPolicy, disposition_action: e.target.value })}
                    className="input"
                  >
                    <option value="review">Review before disposal</option>
                    <option value="archive">Auto-archive</option>
                    <option value="delete">Auto-delete</option>
                  </select>
                </div>
                <div>
                  <label className="label">Applies to Classification</label>
                  <input
                    value={newPolicy.applies_to_classification}
                    onChange={(e) => setNewPolicy({ ...newPolicy, applies_to_classification: e.target.value })}
                    className="input" placeholder="e.g. health_safety"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowPolicyModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={!newPolicy.name || createPolicyMutation.isPending} className="btn-primary">
                  {createPolicyMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4" /> Create Policy</>}
                </button>
              </div>
              {createPolicyMutation.isError && <p className="text-sm text-red-600">Failed to create policy.</p>}
            </form>
          </div>
        </div>
      )}

      {/* Create Legal Hold Modal */}
      {showHoldModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800 flex items-center gap-2">
                <Lock className="w-5 h-5 text-red-500" />
                Create Legal Hold
              </h2>
              <button onClick={() => setShowHoldModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (!newHold.name || !newHold.reason) return
                const payload: Record<string, unknown> = { name: newHold.name, reason: newHold.reason }
                if (newHold.description) payload.description = newHold.description
                createHoldMutation.mutate(payload)
              }}
              className="space-y-4"
            >
              <div>
                <label className="label">Hold Name *</label>
                <input
                  value={newHold.name}
                  onChange={(e) => setNewHold({ ...newHold, name: e.target.value })}
                  className="input" placeholder="e.g. Smith Guest Injury Claim — Room 204" required
                />
              </div>
              <div>
                <label className="label">Reason *</label>
                <input
                  value={newHold.reason}
                  onChange={(e) => setNewHold({ ...newHold, reason: e.target.value })}
                  className="input" placeholder="Legal basis for the hold" required
                />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea
                  value={newHold.description}
                  onChange={(e) => setNewHold({ ...newHold, description: e.target.value })}
                  rows={3} className="input resize-none"
                  placeholder="Details about which documents to hold and why..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowHoldModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={!newHold.name || !newHold.reason || createHoldMutation.isPending} className="btn-primary">
                  {createHoldMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><Lock className="w-4 h-4" /> Create Hold</>}
                </button>
              </div>
              {createHoldMutation.isError && <p className="text-sm text-red-600">Failed to create legal hold.</p>}
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
